"use strict";

window.CartQuantityLimit = {
  LIMIT: 10,
  WAREHOUSE_KEYS: ["central", "partner"],
  HELD_STORAGE_KEY: "azparts:cart-held-over-limit-qty",
  variantWarehouseMap: new Map(),
  /** lineKey → customer qty while warehouse is over LIMIT (Ảnh 1 error state). */
  heldLineQuantities: new Map(),
  /** variantId → qty (stable across refresh / line-key changes). */
  heldByVariant: new Map(),
  /** Warehouse keys whose over-limit popup has already been shown for this state. */
  overLimitNotified: new Set(),
  _warehouseMapPromise: null,
  _heldShopifyPersistInFlight: false,
  _heldRestoreDone: false,

  isOverLimit(total) {
    return total > this.LIMIT;
  },

  normalizeWarehouseKey(value) {
    return value === "central" || value === "partner" ? value : "central";
  },

  invalidateWarehouseMap() {
    this._warehouseMapPromise = null;
  },

  readHeldStorage() {
    try {
      const raw = localStorage.getItem(this.HELD_STORAGE_KEY);
      if (!raw) return { byKey: {}, byVariant: {} };
      const parsed = JSON.parse(raw);
      return {
        byKey: parsed?.byKey && typeof parsed.byKey === "object" ? parsed.byKey : {},
        byVariant:
          parsed?.byVariant && typeof parsed.byVariant === "object" ? parsed.byVariant : {},
      };
    } catch (error) {
      return { byKey: {}, byVariant: {} };
    }
  },

  persistHeldToStorage() {
    const byKey = {};
    const byVariant = {};
    this.heldLineQuantities.forEach((qty, key) => {
      byKey[String(key)] = parseInt(qty, 10) || 0;
    });
    this.heldByVariant.forEach((qty, variantId) => {
      byVariant[String(variantId)] = parseInt(qty, 10) || 0;
    });

    try {
      if (!Object.keys(byKey).length && !Object.keys(byVariant).length) {
        localStorage.removeItem(this.HELD_STORAGE_KEY);
        return;
      }
      localStorage.setItem(
        this.HELD_STORAGE_KEY,
        JSON.stringify({ byKey, byVariant, savedAt: Date.now() }),
      );
    } catch (error) {
      console.warn("Unable to persist cart over-limit quantities", error);
    }
  },

  loadHeldFromStorage() {
    const { byKey, byVariant } = this.readHeldStorage();
    this.heldLineQuantities.clear();
    this.heldByVariant.clear();

    Object.entries(byKey).forEach(([key, qty]) => {
      const n = parseInt(qty, 10) || 0;
      if (n > 0) this.heldLineQuantities.set(String(key), n);
    });
    Object.entries(byVariant).forEach(([variantId, qty]) => {
      const n = parseInt(qty, 10) || 0;
      if (n > 0) this.heldByVariant.set(String(variantId), n);
    });
  },

  /** Map stored variant holds onto current line keys after navigation/refresh. */
  remapHeldKeysFromDom(root) {
    const scopes = [
      document.querySelector("#main-cart-items .js-contents"),
      document.querySelector("#header-mini-cart-content .drawer-content-inner"),
      root && root !== document ? this.resolveCartQuantityScope(root) : null,
    ].filter(Boolean);

    const seen = new Set();
    scopes.forEach((scope) => {
      scope.querySelectorAll(".js-cart-item").forEach((item) => {
        const input = item.querySelector('[name="updates[]"]');
        const key = input?.dataset?.key;
        const variantId = item.dataset.variantId;
        if (!key || !variantId || seen.has(String(key))) return;
        seen.add(String(key));

        const heldVariantQty = this.heldByVariant.get(String(variantId));
        const serverQty =
          parseInt(item.dataset.serverQuantity || item.dataset.quantity || "0", 10) || 0;

        if (heldVariantQty && heldVariantQty > serverQty) {
          this.heldLineQuantities.set(String(key), heldVariantQty);
        } else if (heldVariantQty && heldVariantQty <= serverQty) {
          // Server already has the held qty (or more) — drop hold.
          this.heldByVariant.delete(String(variantId));
          this.heldLineQuantities.delete(String(key));
        }
      });
    });

    this.persistHeldToStorage();
  },

  getHeldQtyForItem(item) {
    const input = item?.querySelector?.('[name="updates[]"]');
    const key = input?.dataset?.key;
    const variantId = item?.dataset?.variantId;
    if (key && this.heldLineQuantities.has(String(key))) {
      return this.heldLineQuantities.get(String(key));
    }
    if (variantId && this.heldByVariant.has(String(variantId))) {
      return this.heldByVariant.get(String(variantId));
    }
    return null;
  },

  ingestWarehouseNodes(root) {
    const scope = root || document;
    scope.querySelectorAll("[data-warehouse-key], [data-cart-warehouse-key]").forEach((node) => {
      const variantId = node.dataset.variantId;
      const warehouseKey = node.dataset.warehouseKey || node.dataset.cartWarehouseKey;
      if (variantId && (warehouseKey === "central" || warehouseKey === "partner")) {
        this.variantWarehouseMap.set(String(variantId), warehouseKey);
      }
    });
  },

  async ensureWarehouseMap() {
    this.ingestWarehouseNodes(document);

    if (this._warehouseMapPromise) {
      return this._warehouseMapPromise;
    }

    const cartUrl = `${window.Shopify?.routes?.cart_url || "/cart"}?section_id=cart-warehouse-keys&_=${Date.now()}`;

    this._warehouseMapPromise = fetch(cartUrl, { cache: "no-store" })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Warehouse map request failed (${response.status})`);
        }
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        this.ingestWarehouseNodes(doc);
      })
      .catch((error) => {
        console.warn("Unable to load cart warehouse map", error);
      });

    return this._warehouseMapPromise;
  },

  getVariantWarehouseKey(variantId) {
    const variantIdString = String(variantId);

    if (this.variantWarehouseMap.has(variantIdString)) {
      return this.normalizeWarehouseKey(this.variantWarehouseMap.get(variantIdString));
    }

    const cartItem = document.querySelector(
      `.js-cart-item[data-variant-id="${variantIdString}"][data-warehouse]`,
    );
    if (cartItem?.dataset.warehouse) {
      return this.normalizeWarehouseKey(cartItem.dataset.warehouse);
    }

    const variantNode = document.querySelector(
      `[data-variant-id="${variantIdString}"][data-warehouse-key], [data-variant-id="${variantIdString}"][data-cart-warehouse-key]`,
    );
    if (variantNode?.dataset.warehouseKey) {
      return this.normalizeWarehouseKey(variantNode.dataset.warehouseKey);
    }
    if (variantNode?.dataset.cartWarehouseKey) {
      return this.normalizeWarehouseKey(variantNode.dataset.cartWarehouseKey);
    }

    return "central";
  },

  getCartWarehouseQuantity(cart, warehouseKey) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce((total, item) => {
      const itemWarehouse = this.getVariantWarehouseKey(item.variant_id);
      if (itemWarehouse !== warehouseKey) return total;
      return total + item.quantity;
    }, 0);
  },

  async getCartWarehouseQuantityAsync(cart, warehouseKey) {
    await this.ensureWarehouseMap();
    return this.getCartWarehouseQuantity(cart, warehouseKey);
  },

  getLineWarehouseKey(line) {
    const lineItem =
      document.getElementById(`MiniCartItem-${line}`) ||
      document.querySelector(`.js-cart-item [data-index="${line}"]`)?.closest(".js-cart-item");

    return this.normalizeWarehouseKey(lineItem?.dataset.warehouse || "central");
  },

  getPrimaryCartContainer() {
    const cartPageItems = document.querySelector("#main-cart-items .js-contents");
    const miniCartItems = document.querySelector("#header-mini-cart-content .drawer-content-inner");

    if (cartPageItems?.querySelector(".js-cart-item")) {
      return cartPageItems;
    }

    if (miniCartItems?.querySelector(".js-cart-item")) {
      return miniCartItems;
    }

    return cartPageItems || miniCartItems || null;
  },

  resolveCartQuantityScope(root) {
    if (!root || root === document) {
      return this.getPrimaryCartContainer() || document;
    }

    if (root.matches?.(".js-contents, .drawer-content-inner")) {
      return root;
    }

    return (
      root.querySelector?.(".js-contents, .drawer-content-inner") ||
      root.closest?.("#main-cart-items .js-contents, #header-mini-cart-content .drawer-content-inner") ||
      root
    );
  },

  getLineQuantityFromDom(item) {
    const input = item?.querySelector?.('[name="updates[]"]');
    const held = this.getHeldQtyForItem(item);
    const committed = parseInt(item?.dataset?.quantity || "0", 10) || 0;
    const typed = parseInt(input?.value || "0", 10) || 0;
    // Keep over-limit error state (Ảnh 1): prefer held/typed qty, never clamp to 10.
    return Math.max(held || 0, typed, committed);
  },

  findInputByLineKey(lineKey) {
    if (!lineKey) return null;
    return (
      Array.from(document.querySelectorAll('[name="updates[]"]')).find(
        (input) => String(input.dataset.key) === String(lineKey),
      ) || null
    );
  },

  /** Remember customer-entered qty when warehouse is over LIMIT (do not reset to 10). */
  holdLineQuantity(lineKey, qty, variantId) {
    if (!lineKey && !variantId) return;
    const n = parseInt(qty, 10) || 0;

    if (n <= 0) {
      if (lineKey) this.heldLineQuantities.delete(String(lineKey));
      if (variantId) this.heldByVariant.delete(String(variantId));
      this.persistHeldToStorage();
      return;
    }

    if (lineKey) this.heldLineQuantities.set(String(lineKey), n);

    let resolvedVariantId = variantId;
    if (!resolvedVariantId && lineKey) {
      resolvedVariantId = this.findInputByLineKey(lineKey)?.closest(".js-cart-item")?.dataset
        ?.variantId;
    }
    if (resolvedVariantId) {
      this.heldByVariant.set(String(resolvedVariantId), n);
    }

    this.persistHeldToStorage();
  },

  clearHeldLineQuantity(lineKey, variantId) {
    if (lineKey) this.heldLineQuantities.delete(String(lineKey));

    let resolvedVariantId = variantId;
    if (!resolvedVariantId && lineKey) {
      resolvedVariantId = this.findInputByLineKey(lineKey)?.closest(".js-cart-item")?.dataset
        ?.variantId;
    }
    if (resolvedVariantId) this.heldByVariant.delete(String(resolvedVariantId));

    this.persistHeldToStorage();
  },

  isHoldingLine(lineKey) {
    if (lineKey && this.heldLineQuantities.has(String(lineKey))) return true;
    return false;
  },

  /** After section re-render: put held over-limit qty back on inputs (Ảnh 1). */
  applyHeldQuantitiesToDom(root) {
    this.remapHeldKeysFromDom(root);

    const applyToScope = (scope) => {
      if (!scope) return;

      scope.querySelectorAll(".js-cart-item").forEach((item) => {
        const input = item.querySelector('[name="updates[]"]');
        const key = input?.dataset?.key;
        const held = this.getHeldQtyForItem(item);
        if (!held || !input) return;

        const qty = String(held);
        if (String(input.value) !== qty) {
          input.value = qty;
        }
        if (!item.dataset.serverQuantity) {
          item.dataset.serverQuantity = item.dataset.quantity || qty;
        }
        item.dataset.quantity = qty;
        input.dataset.cartQtyBaseline = qty;
        input.setAttribute("data-previous-value", qty);

        if (key && !this.heldLineQuantities.has(String(key))) {
          this.heldLineQuantities.set(String(key), held);
        }
      });
    };

    if (!this.heldLineQuantities.size && !this.heldByVariant.size) {
      if (typeof window.refreshCartWarehouseSummaries === "function") {
        window.refreshCartWarehouseSummaries();
      }
      return;
    }

    // Apply to both main cart + mini-cart so badge/totals stay in sync.
    if (root && root !== document) {
      applyToScope(this.resolveCartQuantityScope(root));
    }
    applyToScope(document.querySelector("#main-cart-items .js-contents"));
    applyToScope(document.querySelector("#header-mini-cart-content .drawer-content-inner"));

    if (typeof window.refreshCartWarehouseSummaries === "function") {
      window.refreshCartWarehouseSummaries();
    }
  },

  /**
   * Push held over-limit qtys to Shopify cart/update so they persist server-side when inventory allows.
   * If Shopify caps (availability), localStorage hold still keeps the error-state UI.
   */
  async persistHeldQuantitiesToShopify() {
    if (this._heldShopifyPersistInFlight) return;
    if (!this.heldByVariant.size && !this.heldLineQuantities.size) return;

    const updates = {};
    const scopes = [
      document.querySelector("#main-cart-items .js-contents"),
      document.querySelector("#header-mini-cart-content .drawer-content-inner"),
    ].filter(Boolean);
    const seenVariants = new Set();

    scopes.forEach((scope) => {
      scope.querySelectorAll(".js-cart-item").forEach((item) => {
        const variantId = item.dataset.variantId;
        if (!variantId || seenVariants.has(String(variantId))) return;
        seenVariants.add(String(variantId));

        const held = this.getHeldQtyForItem(item);
        const serverQty =
          parseInt(item.dataset.serverQuantity || item.dataset.quantity || "0", 10) || 0;
        if (held && held > serverQty) {
          updates[String(variantId)] = held;
        }
      });
    });

    if (!Object.keys(updates).length) return;

    this._heldShopifyPersistInFlight = true;
    try {
      const cartUrl = (window.Shopify?.routes?.cart_url || "/cart").replace(/\/$/, "");
      const response = await fetch(`${cartUrl}/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) return;

      const cart = await response.json();
      (cart.items || []).forEach((item) => {
        const savedQty = parseInt(item.quantity, 10) || 0;
        const variantId = String(item.variant_id);
        const held = this.heldByVariant.get(variantId);
        // If Shopify accepted the held qty, keep hold in sync; if capped lower, keep held for UI.
        if (held && savedQty >= held) {
          // Fully persisted on server — hold can stay as same number for UI consistency.
          this.heldByVariant.set(variantId, savedQty);
        }
      });
      this.persistHeldToStorage();
    } catch (error) {
      console.warn("Unable to persist over-limit cart quantities to Shopify", error);
    } finally {
      this._heldShopifyPersistInFlight = false;
    }
  },

  async restoreHeldState(root) {
    this.loadHeldFromStorage();
    this.remapHeldKeysFromDom(root);
    this.applyHeldQuantitiesToDom(root);

    if (!this._heldRestoreDone) {
      this._heldRestoreDone = true;
      await this.persistHeldQuantitiesToShopify();
      this.remapHeldKeysFromDom(root);
      this.applyHeldQuantitiesToDom(root);
    }

    this.applyCartQuantityLimitUIFromDom(document, false);
  },

  /**
   * After a qty change: if this warehouse is over LIMIT, hold the entered qty
   * so UI stays in error state (banner + disabled checkout). Otherwise clear hold.
   */
  syncHeldQuantityAfterChange(root, lineIndex, lineKey, requestedQty) {
    const warehouseKey = this.getLineWarehouseKey(lineIndex);
    const projected = this.getProjectedWarehouseTotalFromDom(
      root,
      lineIndex,
      requestedQty,
      warehouseKey,
    );
    const input = document.querySelector(`[name="updates[]"][data-index="${lineIndex}"]`);
    const variantId = input?.closest(".js-cart-item")?.dataset?.variantId;

    if (this.isOverLimit(projected) && (parseInt(requestedQty, 10) || 0) > 0) {
      this.holdLineQuantity(lineKey, requestedQty, variantId);
    } else {
      this.clearHeldLineQuantity(lineKey, variantId);
    }
  },

  getWarehouseQuantityFromDom(warehouseKey, root) {
    const scope = this.resolveCartQuantityScope(root);
    let total = 0;

    scope.querySelectorAll(".js-cart-item").forEach((item) => {
      const itemWarehouse = this.normalizeWarehouseKey(item.dataset.warehouse || "central");
      if (itemWarehouse !== warehouseKey) return;
      total += this.getLineQuantityFromDom(item);
    });

    return total;
  },

  getProjectedWarehouseTotalFromDom(root, lineIndex, newQty, warehouseKey) {
    const scope = this.resolveCartQuantityScope(root);
    let total = 0;

    scope.querySelectorAll(".js-cart-item").forEach((item) => {
      const input = item.querySelector('[name="updates[]"]');
      const line = input?.dataset.index;
      const itemWarehouse = this.normalizeWarehouseKey(item.dataset.warehouse || "central");

      if (itemWarehouse !== warehouseKey) return;

      if (String(line) === String(lineIndex)) {
        total += parseInt(newQty, 10) || 0;
      } else {
        total += this.getLineQuantityFromDom(item);
      }
    });

    return total;
  },

  getWarehouseKeysInDom(root) {
    const scope = this.resolveCartQuantityScope(root);
    const keys = new Set();

    scope.querySelectorAll(".js-cart-item").forEach(function (item) {
      keys.add(window.CartQuantityLimit.normalizeWarehouseKey(item.dataset.warehouse || "central"));
    });

    if (!keys.size) {
      keys.add("central");
    }

    return Array.from(keys);
  },

  getMessage() {
    return (
      window.cartStrings?.quantityLimit ||
      "You can only add up to 10 items per order. Please remove some items before checking out"
    );
  },

  /**
   * AZP-7304: the over-limit warning is a popup, not an inline banner.
   *
   * The popup is customer-triggered only: it opens when the customer changes the cart
   * (add / quantity / remove) or clicks a blocked Checkout. Page loads, navigation,
   * section re-renders and background cart syncs call this with `shouldNotify` false —
   * they refresh the disabled states silently and deliberately record nothing, so the
   * next real cart change still raises the popup.
   *
   * Within one over-limit episode the popup is raised once per warehouse; the flag
   * resets as soon as that warehouse is back under the cap.
   */
  notifyOverLimit(warehouseKey, isOverLimit, shouldNotify) {
    if (!isOverLimit) {
      this.overLimitNotified.delete(warehouseKey);
      return;
    }

    if (!shouldNotify) return;
    if (this.overLimitNotified.has(warehouseKey)) return;
    this.overLimitNotified.add(warehouseKey);

    if (typeof window.openCartQuantityLimitModal === "function") {
      window.openCartQuantityLimitModal();
    }
  },

  applyWarehouseCheckoutButtons(warehouseKey, isOverLimit) {
    document
      .querySelectorAll(`[data-warehouse-checkout="${warehouseKey}"]`)
      .forEach(function (button) {
        button.classList.toggle("button--disabled", isOverLimit);

        if (isOverLimit) {
          button.setAttribute("aria-disabled", "true");
          button.setAttribute("tabindex", "-1");
        } else {
          button.removeAttribute("aria-disabled");
          button.removeAttribute("tabindex");
          button.removeAttribute("disabled");
        }
      });
  },

  /** Disable cart "+" when this warehouse total is already > LIMIT. Manual input stays allowed. */
  applyWarehousePlusButtons(warehouseKey, isOverLimit, root) {
    const scope = this.resolveCartQuantityScope(root || document);

    scope.querySelectorAll(".js-cart-item").forEach((item) => {
      const itemWarehouse = this.normalizeWarehouseKey(item.dataset.warehouse || "central");
      if (itemWarehouse !== warehouseKey) return;

      const plusButton = item.querySelector(
        'quantity-input button[name="plus"], button.quantity-input-plus[name="plus"]',
      );
      if (!plusButton) return;

      plusButton.disabled = isOverLimit;
      plusButton.classList.toggle("quantity-input-button--disabled", isOverLimit);
      if (isOverLimit) {
        plusButton.setAttribute("aria-disabled", "true");
      } else {
        plusButton.removeAttribute("aria-disabled");
      }
    });
  },

  applySingleWarehouseFooter(isOverLimit, isCartEmpty) {
    const checkoutButton = document.querySelector(".mini-cart-footer-actions button[name='checkout']");
    if (checkoutButton) {
      // Over-limit uses aria-disabled + the disabled class, never the native attribute:
      // a natively disabled button swallows the click, and we need that click to open
      // the popup that explains why checkout is blocked.
      checkoutButton.disabled = isCartEmpty;
      checkoutButton.classList.toggle("button--disabled", isOverLimit);
      if (isOverLimit) {
        checkoutButton.setAttribute("aria-disabled", "true");
      } else {
        checkoutButton.removeAttribute("aria-disabled");
      }
    }
    // The popup is raised per warehouse key by notifyOverLimit(); this footer covers the
    // same items under the "single" alias, so notifying here would double-fire.
  },

  applyMainCartCheckout(isOverLimit, isCartEmpty) {
    const checkoutButton = document.getElementById("checkout");
    if (!checkoutButton) return;

    // See applySingleWarehouseFooter(): keep the click alive so it can open the popup.
    checkoutButton.disabled = isCartEmpty;
    checkoutButton.classList.toggle("button--disabled", isOverLimit);
    if (isOverLimit) {
      checkoutButton.setAttribute("aria-disabled", "true");
    } else {
      checkoutButton.removeAttribute("aria-disabled");
    }
  },

  /** `shouldNotify` is true only for customer-driven cart changes — see notifyOverLimit(). */
  applyCartQuantityLimitUIFromDom(root, shouldNotify) {
    this.applyHeldQuantitiesToDom(root);
    const scope = this.resolveCartQuantityScope(root);
    const presentKeys = this.getWarehouseKeysInDom(scope);
    const isMultiWarehouse = presentKeys.length > 1;
    const empty = !scope.querySelector(".js-cart-item");

    // Refresh each warehouse independently — fixing A must not clear B's held error state.
    this.WAREHOUSE_KEYS.forEach((warehouseKey) => {
      const quantity = this.getWarehouseQuantityFromDom(warehouseKey, scope);
      const isOverLimit = this.isOverLimit(quantity);

      this.notifyOverLimit(warehouseKey, isOverLimit, shouldNotify);
      this.applyWarehouseCheckoutButtons(warehouseKey, isOverLimit);
      this.applyWarehousePlusButtons(warehouseKey, isOverLimit, scope);
    });

    if (!isMultiWarehouse) {
      const quantity = this.getWarehouseQuantityFromDom(presentKeys[0] || "central", scope);
      const isOverLimit = this.isOverLimit(quantity);
      this.applySingleWarehouseFooter(isOverLimit, empty);
      this.applyMainCartCheckout(isOverLimit, empty);
    } else {
      this.applyMainCartCheckout(false, false);
      this.applySingleWarehouseFooter(false, false);
    }
  },

  /** `shouldNotify` is true only for customer-driven cart changes — see notifyOverLimit(). */
  applyCartQuantityLimitUI(cart, shouldNotify) {
    // Prefer DOM after section re-render — cart.js + incomplete warehouse map
    // was incorrectly zeroing the other warehouse and hiding its alert.
    const applyFromDomOrCart = () => {
      if (document.querySelector(".js-cart-item")) {
        this.applyCartQuantityLimitUIFromDom(document, shouldNotify);
        return;
      }

      if (!cart || !Array.isArray(cart.items)) {
        this.applyMainCartCheckout(false, true);
        this.applySingleWarehouseFooter(false, true);
        return;
      }

      this.WAREHOUSE_KEYS.forEach((warehouseKey) => {
        const quantity = this.getCartWarehouseQuantity(cart, warehouseKey);
        const isOverLimit = this.isOverLimit(quantity);
        this.notifyOverLimit(warehouseKey, isOverLimit, shouldNotify);
        this.applyWarehouseCheckoutButtons(warehouseKey, isOverLimit);
        this.applyWarehousePlusButtons(warehouseKey, isOverLimit, document);
      });

      const warehouseKeys = new Set(
        cart.items.map((item) => this.getVariantWarehouseKey(item.variant_id)),
      );

      if (warehouseKeys.size <= 1) {
        const warehouseKey = Array.from(warehouseKeys)[0] || "central";
        const quantity = this.getCartWarehouseQuantity(cart, warehouseKey);
        const isOverLimit = this.isOverLimit(quantity);
        const isCartEmpty = !cart.items.length;
        this.applySingleWarehouseFooter(isOverLimit, isCartEmpty);
        this.applyMainCartCheckout(isOverLimit, isCartEmpty);
      } else {
        this.applyMainCartCheckout(false, false);
        this.applySingleWarehouseFooter(false, false);
      }
    };

    if (cart && Array.isArray(cart.items)) {
      this.ensureWarehouseMap().then(applyFromDomOrCart);
    } else {
      applyFromDomOrCart();
    }

    if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
      this.applyMainCartCheckout(false, true);
      this.applySingleWarehouseFooter(false, true);
    }

    return false;
  },

  syncProjectedQuantityUI(cartRoot, lineIndex, newQty) {
    const scope = this.resolveCartQuantityScope(cartRoot);
    const editedWarehouse = this.getLineWarehouseKey(lineIndex);
    const input = scope.querySelector(`[name="updates[]"][data-index="${lineIndex}"]`);
    const lineKey = input?.dataset?.key;

    // While typing: keep held qty so warehouse subtotal uses the entered number.
    if (lineKey) {
      const projectedEdited = this.getProjectedWarehouseTotalFromDom(
        scope,
        lineIndex,
        newQty,
        editedWarehouse,
      );
      const variantId = input?.closest(".js-cart-item")?.dataset?.variantId;
      if (this.isOverLimit(projectedEdited) && (parseInt(newQty, 10) || 0) > 0) {
        this.holdLineQuantity(lineKey, newQty, variantId);
      } else {
        this.clearHeldLineQuantity(lineKey, variantId);
      }
    }

    // Re-assert every warehouse: project only the edited line's warehouse,
    // keep other warehouses' real DOM totals (so A stays over-limit while editing B).
    this.WAREHOUSE_KEYS.forEach((warehouseKey) => {
      const quantity =
        warehouseKey === editedWarehouse
          ? this.getProjectedWarehouseTotalFromDom(scope, lineIndex, newQty, warehouseKey)
          : this.getWarehouseQuantityFromDom(warehouseKey, scope);
      const isOverLimit = this.isOverLimit(quantity);

      // Reached from the quantity input / plus-minus buttons — always a customer action.
      this.notifyOverLimit(warehouseKey, isOverLimit, true);
      this.applyWarehouseCheckoutButtons(warehouseKey, isOverLimit);
      this.applyWarehousePlusButtons(warehouseKey, isOverLimit, scope);
    });

    const warehouseKeys = this.getWarehouseKeysInDom(scope);
    if (warehouseKeys.length <= 1) {
      const quantity = this.getProjectedWarehouseTotalFromDom(
        scope,
        lineIndex,
        newQty,
        editedWarehouse,
      );
      const isOverLimit = this.isOverLimit(quantity);
      this.applySingleWarehouseFooter(isOverLimit, false);
      this.applyMainCartCheckout(isOverLimit, false);
    }

    if (typeof window.refreshCartWarehouseSummaries === "function") {
      window.refreshCartWarehouseSummaries();
    }
  },
};

(function () {
  const CART_API_URLS = ["/cart/change", "/cart/update", "/cart/add", "/cart/clear"];
  let cartUISyncQueue = Promise.resolve();

  const isCartApiRequest = function (requestUrl) {
    return CART_API_URLS.some(function (url) {
      return requestUrl.includes(url);
    });
  };

  const enqueueCartUISync = function (task) {
    const run = cartUISyncQueue.then(task);
    cartUISyncQueue = run.catch(function (error) {
      console.error("Cart quantity limit UI sync failed", error);
    });
    return run;
  };

  window.openCartQuantityLimitModal = function () {
    const modal = document.getElementById("cart-quantity-limit-modal");
    if (!modal) return;

    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("cart-quantity-limit-modal-open");
    document.body.classList.add("cart-quantity-limit-modal-open");
  };

  window.closeCartQuantityLimitModal = function (event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const modal = document.getElementById("cart-quantity-limit-modal");
    if (!modal) return;

    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("cart-quantity-limit-modal-open");
    document.body.classList.remove("cart-quantity-limit-modal-open");
  };

  const fetchLatestCartAndApplyUI = async function (shouldNotify) {
    const response = await fetch("/cart.js", { cache: "no-store" });
    if (!response.ok) return;

    const cart = await response.json();
    await window.CartQuantityLimit.ensureWarehouseMap();
    window.CartQuantityLimit.applyCartQuantityLimitUI(cart, shouldNotify);
  };

  const syncUIAfterCartResponse = async function (response, shouldNotify) {
    if (!response || !response.ok) return;

    try {
      const cart = await response.clone().json();
      if (cart && Array.isArray(cart.items)) {
        window.CartQuantityLimit.applyCartQuantityLimitUI(cart, shouldNotify);
        return;
      }
    } catch (error) {
      // Response is not JSON cart payload, fallback below.
    }

    await fetchLatestCartAndApplyUI(shouldNotify);
  };

  // Public refresh hook: re-syncs the disabled states without ever raising the popup.
  window.syncCartQuantityLimitUI = function () {
    return fetchLatestCartAndApplyUI(false);
  };

  document.addEventListener("click", function (event) {
    if (event.target.closest("#cart-quantity-limit-modal-confirm")) {
      window.closeCartQuantityLimitModal(event);
    }

    const modal = document.getElementById("cart-quantity-limit-modal");
    if (modal && event.target === modal) {
      window.closeCartQuantityLimitModal(event);
    }
  });

  document.addEventListener(
    "input",
    function (event) {
      const input = event.target.closest('[name="updates[]"]');
      if (!input || !window.CartQuantityLimit) return;

      const cartRoot = input.closest("cart-items, mini-cart, cart-items-component");
      if (!cartRoot) return;

      if (!input.dataset.cartQtyBaseline) {
        input.dataset.cartQtyBaseline =
          input.getAttribute("value") || input.defaultValue || String(input.value || "1");
      }

      window.CartQuantityLimit.syncProjectedQuantityUI(
        cartRoot,
        input.dataset.index,
        input.value,
      );
    },
    true,
  );

  // These only mirror a cart write the fetch patch below has already judged, and they
  // also fire on plain re-renders — refresh the disabled states, never open the popup.
  ["cart:updated", "cart:refresh", "cart:change"].forEach(function (eventName) {
    document.addEventListener(eventName, function () {
      enqueueCartUISync(function () {
        return fetchLatestCartAndApplyUI(false);
      });
    });
  });

  ["cart:item-quantity-update", "cart:item-remove", "cart:item-add"].forEach(function (eventName) {
    document.addEventListener(eventName, function () {
      window.CartQuantityLimit.invalidateWarehouseMap();
      requestAnimationFrame(function () {
        window.CartQuantityLimit.applyCartQuantityLimitUIFromDom(document, false);
      });
    });
  });

  document.addEventListener(
    "click",
    function (event) {
      const disabledCheckout = event.target.closest(
        "a.cart-warehouse-checkout-button.button--disabled, #checkout.button--disabled, button[name='checkout'].button--disabled",
      );
      if (!disabledCheckout) return;

      event.preventDefault();
      event.stopPropagation();

      // Checkout is the moment the customer most needs the reason, so re-open the popup
      // here even when it was already shown for this over-limit state.
      if (typeof window.openCartQuantityLimitModal === "function") {
        window.openCartQuantityLimitModal();
      }
    },
    true,
  );

  document.addEventListener("shopify:section:load", function () {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        enqueueCartUISync(function () {
          return fetchLatestCartAndApplyUI(false);
        });
      });
    });
  });

  if (!window.__cartQuantityLimitFetchPatched) {
    window.__cartQuantityLimitFetchPatched = true;
    const originalFetch = window.fetch;

    window.fetch = function () {
      const args = arguments;
      const requestUrl =
        typeof args[0] === "string" ? args[0] : args[0] && args[0].url ? args[0].url : "";

      // A cart write we issue ourselves (restoring held quantities on load) is not a
      // customer action, so it must not raise the popup. Read the flag at request time:
      // persistHeldQuantitiesToShopify() sets it before its own fetch.
      const shouldNotify = !window.CartQuantityLimit._heldShopifyPersistInFlight;

      return originalFetch.apply(this, args).then(function (response) {
        if (!isCartApiRequest(requestUrl)) return response;

        const responseClone = response.clone();

        enqueueCartUISync(function () {
          window.CartQuantityLimit.invalidateWarehouseMap();

          if (!response.ok) {
            return fetchLatestCartAndApplyUI(shouldNotify);
          }

          return responseClone
            .json()
            .then(function (cart) {
              if (cart && Array.isArray(cart.items)) {
                window.CartQuantityLimit.applyCartQuantityLimitUI(cart, shouldNotify);
              }
            })
            .catch(function () {
              return fetchLatestCartAndApplyUI(shouldNotify);
            });
        });

        return response;
      });
    };
  }

  /** Only the cart template renders #main-cart-items (sections/main-cart-items.liquid). */
  const isCartPage = function () {
    return Boolean(document.getElementById("main-cart-items"));
  };

  // Page load / navigation / reload: restore the blocked checkout + held quantities
  // silently — repeating the popup on every page view is the noise we removed.
  //
  // The cart page is the exception: landing on it is the moment the customer can act on
  // the cap, so a visit there raises the popup. It is notified last, after held
  // quantities are back on the inputs, because the server may have clamped the line to
  // 10 and only the restored quantity shows the warehouse is really over.
  const bootCartQuantityLimitUI = async function () {
    window.CartQuantityLimit.loadHeldFromStorage();
    await fetchLatestCartAndApplyUI(false);

    if (typeof window.resolveCartWarehouses === "function") {
      await window.resolveCartWarehouses();
    }

    await window.CartQuantityLimit.restoreHeldState(document);

    if (isCartPage()) {
      window.CartQuantityLimit.applyCartQuantityLimitUIFromDom(document, true);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootCartQuantityLimitUI);
  } else {
    bootCartQuantityLimitUI();
  }
})();
